/**
 * Per-language message builders for the All-E eligibility engine.
 *
 * The calculator composes ~35 human-readable detail/disclaimer sentences.
 * Dutch ("nl") is the default and MUST stay byte-identical to the historical
 * inline templates (unit tests assert Dutch substrings; stored submissions
 * keep their original text). English ("en") mirrors every builder.
 *
 * Static Dutch fragments that originate in config.ts (WATER_TEMP_RULES
 * details, option labels) are translated via lookup maps keyed by the Dutch
 * source string -- same mechanism as the UI's Dutch-string-as-key i18n.
 */

import { DISCLAIMERS } from "./config";

export type Lang = "nl" | "en";

export interface EngineDisclaimers {
  constant_temp: string;
  partial_coverage: (coveragePct: number, maxTemp: number) => string;
  water_temp_warning: string;
  thermostat_warning: string;
  gas_cap: string;
  label_block: (label: string) => string;
  insulation_advisory: string;
  insulation_critical: string;
  renovation_block: string;
  heatbattery_space: string;
  no_outdoor_placement: string;
  piping_long: string;
  beton_wall: string;
  no_electrical_group: string;
  panel_replacement: string;
  meerwerk_l_tier: string;
  stadsverwarming: string;
  luchtverwarming: string;
  convectoren_only: string;
  apartment_no_vve: string;
  apartment_no_space: string;
}

export interface EngineMessages {
  d: EngineDisclaimers;
  no_exclusions: string;
  gas_cap_detail: (m3: number, capM3: number) => string;
  label_block_detail: (label: string) => string;
  heat_ok_single: (req: number, cap: number, waterTemp: number, bucket: string) => string;
  heat_ok_duo: (req: number, cap: number, waterTemp: number, bucket: string) => string;
  heat_duo_required: (cov: number, maxTemp: number) => string;
  heat_duo_limited: (cov: number, maxTemp: number) => string;
  heat_not_suitable: (cov: number, maxTemp: number) => string;
  water_combo_check: (main: string, whole: string, comfort: boolean) => string;
  water_fill_radiators: (baseDetail: string) => string;
  rad_no_data: string;
  rad_ok: (total: string, needed: string, pct: number) => string;
  rad_margin: (total: string, needed: string, pct: number) => string;
  rad_insufficient: (total: string, needed: string, pct: number) => string;
  rad_combined: (baseDetail: string, radDetail: string) => string;
  th_zone: string;
  th_extra_sources: string;
  th_cw6: string;
  th_day_low: string;
  th_day_high: string;
  th_delta: (diff: number) => string;
  th_not_suitable: (red: string) => string;
  th_additional: (amber: string) => string;
  th_extra_check: (amber: string) => string;
  th_ok: (day: number, night: number, delta: number) => string;
  ins_default_ok: string;
  ins_score_low: (avg: string) => string;
  ins_score_mid: (avg: string) => string;
  ins_score_good: (avg: string) => string;
  ins_label_despite_score: (label: string, avg: string) => string;
  ins_label_block: (label: string) => string;
  ins_pre1970: (count: number) => string;
  ins_year_min: (yearLabel: string, count: number, min: number) => string;
  ins_no_data: string;
  unknown: string;
  phys_hb_doubt: string;
  phys_ok: string;
  elec_ok: string;
  mw_std: string;
  mw_s: (factor: string) => string;
  mw_m: (count: number) => string;
  mw_factor_outdoor: (label: string) => string;
  mw_factor_piping: (label: string) => string;
  mw_factor_wall: (label: string) => string;
  mw_no_group: string;
  mw_panel: string;
  mw_panel_extra: string;
  mw_contact: string;
  /** Translate a static Dutch config string (water-temp rule details, option labels). */
  tr: (dutch: string) => string;
}

// English versions of static Dutch strings that live in config.ts
const EN_CONFIG_STRINGS: Record<string, string> = {
  // WATER_TEMP_RULES details
  "Vloerverwarming is een lage temperatuur systeem, geschikt voor All-Electric.":
    "Underfloor heating is a low-temperature system, suitable for All-Electric.",
  "CV comfortabel op 55C met radiatoren, geschikt voor All-Electric.":
    "Boiler comfortable at 55C with radiators, suitable for All-Electric.",
  "Radiatoren zonder comfort op 55C: extra controle nodig. Neem contact op met het quality team.":
    "Radiators without comfort at 55C: additional review needed. Contact the quality team.",
  "Vloerverwarming met aanvullende bronnen, comfortabel op 55C. Geschikt voor All-Electric.":
    "Underfloor heating with supplementary sources, comfortable at 55C. Suitable for All-Electric.",
  "Gemengd systeem zonder comfort op 55C: extra controle nodig voor de aanvullende bronnen.":
    "Mixed system without comfort at 55C: additional review needed for the supplementary sources.",
  // Option labels (meerwerk factors, build years)
  "Begane grond": "Ground floor",
  "Muur tot 4m hoogte": "Wall up to 4m height",
  "Muur 4-8m hoogte": "Wall 4-8m height",
  "Dak of dakkapel": "Roof or dormer",
  "Niet mogelijk": "Not possible",
  "0-10 meter": "0-10 metres",
  "10-15 meter": "10-15 metres",
  "15-20 meter": "15-20 metres",
  "Meer dan 20 meter": "More than 20 metres",
  "Weet ik niet": "Don't know",
  "Baksteen of spouwmuur": "Brick or cavity wall",
  "Beton": "Concrete",
  "Anders": "Other",
  "Voor 1970": "Before 1970",
  "Na 2005": "After 2005",
  // Distribution values (appear verbatim in the water-temp fallback detail)
  "Radiatoren": "Radiators",
  "Vloerverwarming": "Underfloor heating",
  "Vloerverwarming + andere bronnen": "Underfloor heating + other sources",
};

const NL: EngineMessages = {
  d: DISCLAIMERS,
  no_exclusions: "Geen uitsluitingen gevonden.",
  gas_cap_detail: (m3, capM3) =>
    `Jaarlijks gasverbruik ${m3} m3 overschrijdt de limiet van ${capM3} m3. De warmtevraag is te hoog voor All-Electric.`,
  label_block_detail: (label) =>
    `Energielabel ${label} duidt op onvoldoende isolatie voor All-Electric. Aanvullende isolatiemaatregelen zijn nodig.`,
  heat_ok_single: (req, cap, waterTemp, bucket) =>
    `Geschikt: Single. Warmtevraag ${req} kW past binnen Single capaciteit ${cap} kW (W${waterTemp}, isolatie ${bucket}).`,
  heat_ok_duo: (req, cap, waterTemp, bucket) =>
    `Geschikt: Duo. Warmtevraag ${req} kW past binnen Duo capaciteit ${cap} kW (W${waterTemp}, isolatie ${bucket}).`,
  heat_duo_required: (cov, maxTemp) =>
    `Duo vereist (${cov}% dekking). Geschatte max. binnentemperatuur ${maxTemp}°C bij -7°C.`,
  heat_duo_limited: (cov, maxTemp) =>
    `Duo vereist met beperkt comfort (${cov}% dekking). Geschatte max. binnentemperatuur ${maxTemp}°C bij -7°C.`,
  heat_not_suitable: (cov, maxTemp) =>
    `Niet geschikt (${cov}% dekking). Geschatte max. binnentemperatuur ${maxTemp}°C bij -7°C. Aanvullende isolatie is nodig.`,
  water_combo_check: (main, whole, comfort) =>
    `Combinatie ${main} (hoofdruimte) / ${whole} (heel huis) met CV comfort 55C: ${comfort ? "Ja" : "Nee"}. Extra controle nodig.`,
  water_fill_radiators: (baseDetail) =>
    `${baseDetail} Vul radiator-afmetingen in voor exacte controle.`,
  rad_no_data: "Onvoldoende warmtevraaggegevens om radiatorcapaciteit te toetsen.",
  rad_ok: (total, needed, pct) =>
    `${total} kW geleverd >= ${needed} kW benodigd (${pct}%).`,
  rad_margin: (total, needed, pct) =>
    `${total} kW geleverd, ${pct}% van ${needed} kW benodigd (kleine marge).`,
  rad_insufficient: (total, needed, pct) =>
    `${total} kW geleverd, ${pct}% van ${needed} kW benodigd. Radiatoren leveren te weinig bij W55.`,
  rad_combined: (baseDetail, radDetail) => `${baseDetail} Radiatorcheck: ${radDetail}`,
  th_zone:
    "Zone-thermostaat: niet blokkerend, maar All-E heeft minder backup-vermogen dan hybride. " +
    "Zone-regeling tilt watertemperaturen in de praktijk omhoog (lagere COP) en maakt het systeem complexer en storingsgevoeliger. " +
    "Adviseer installateur om mee te kijken.",
  th_extra_sources: "Extra warmtebronnen kunnen sizing beinvloeden",
  th_cw6: "CW6 vraagt hogere piekvraag. Check of dit nodig is",
  th_day_low: "dagtemperatuur lager dan 17C",
  th_day_high: "dagtemperatuur hoger dan 22C",
  th_delta: (diff) => `dag/nacht verschil ${diff}C (max 2C aanbevolen)`,
  th_not_suitable: (red) => `Niet geschikt: ${red}.`,
  th_additional: (amber) => ` Aanvullend: ${amber}.`,
  th_extra_check: (amber) => `Extra controle: ${amber}.`,
  th_ok: (day, night, delta) =>
    `Dag ${day}C / nacht ${night}C (delta ${delta}C). Geschikt voor All-Electric.`,
  ins_default_ok: "Isolatieniveau lijkt voldoende voor All-Electric.",
  ins_score_low: (avg) =>
    `Gemiddelde isolatiescore ${avg}/10. Aanvullende isolatiemaatregelen zijn nodig voor optimaal rendement.`,
  ins_score_mid: (avg) =>
    `Gemiddelde isolatiescore ${avg}/10. Acceptabel; beter resultaat met aanvullende isolatie.`,
  ins_score_good: (avg) => `Gemiddelde isolatiescore ${avg}/10. Goed.`,
  ins_label_despite_score: (label, avg) =>
    `Energielabel ${label} ondanks isolatiescore ${avg}/10. Aanvullende isolatie is nodig.`,
  ins_label_block: (label) =>
    `Energielabel ${label}: isolatie is waarschijnlijk onvoldoende. Aanvullende isolatie is nodig voor All-Electric.`,
  ins_pre1970: (count) =>
    `Woning van voor 1970 met ${count} isolatiemaatregel(en). Minimaal 3 aanbevolen. Bespreek isolatiemogelijkheden met de klant.`,
  ins_year_min: (yearLabel, count, min) =>
    `Woning (${yearLabel}) met ${count} isolatiemaatregel(en). Minimaal ${min} aanbevolen voor optimaal rendement.`,
  ins_no_data: "Geen isolatiegegevens ingevuld. Controleer de isolatie ter plaatse.",
  unknown: "Onbekend",
  phys_hb_doubt: "Twijfel over ruimte voor HeatBattery. Controleer de afmetingen ter plaatse.",
  phys_ok: "Fysieke installatie lijkt haalbaar.",
  elec_ok: "Elektrische aansluiting lijkt in orde.",
  mw_std: "Standaard installatie zonder meerwerk.",
  mw_s: (factor) => `Licht meerwerk verwacht (~€400). Factor: ${factor}.`,
  mw_m: (count) => `Meerwerk verwacht (~€750). ${count} complicerende factoren.`,
  mw_factor_outdoor: (label) => `Buitenunit: ${label}`,
  mw_factor_piping: (label) => `Leidingafstand: ${label}`,
  mw_factor_wall: (label) => `Wandmateriaal: ${label}`,
  mw_no_group: "Geen vrije groep in meterkast",
  mw_panel: "Groepenkast vervanging nodig",
  mw_panel_extra: "Groepenkast vervanging (extra complexiteit)",
  mw_contact: "Neem contact op",
  tr: (dutch) => dutch,
};

const EN_DISCLAIMERS: EngineDisclaimers = {
  constant_temp:
    "The home must be heated to the same temperature 24 hours a day. Zone control (room thermostats per room) is not suitable for All-Electric.",
  partial_coverage: (coveragePct, maxTemp) =>
    `The Hybrid Duo can cover an estimated ${coveragePct}% of the heat demand. At -7C outdoor temperature the estimated maximum indoor temperature is ${maxTemp}C. The customer must accept this loss of comfort.`,
  water_temp_warning:
    "An additional review of the water supply temperature is needed. Contact the quality team before proceeding.",
  thermostat_warning:
    "The difference between day and night temperature is more than 2C. For All-Electric the customer must be willing to keep the same temperature 24 hours a day.",
  gas_cap:
    "The yearly gas consumption is higher than 2,500 m3. The heat demand is too high for All-Electric. A Quatt Hybrid is a better fit.",
  label_block: (label) =>
    `Energy label ${label} indicates insufficient insulation for All-Electric. Additional insulation measures are needed, or a Quatt Hybrid is a better fit.`,
  insulation_advisory:
    "The insulation of this home may be insufficient for optimal All-Electric performance. Additional insulation measures are needed for optimal efficiency.",
  insulation_critical:
    "Older home with little insulation. All-Electric is technically risky. Additional insulation improvements are needed for a safe installation.",
  renovation_block:
    "The home is being renovated or requires a major boiler relocation. Quatt does not perform hydronic balancing.",
  heatbattery_space: "There is insufficient space for the HeatBattery in the boiler room.",
  no_outdoor_placement: "There is no suitable location for the outdoor unit.",
  piping_long: "The piping distance is more than 20 metres. This may not be feasible. Consult Quatt.",
  beton_wall: "A concrete wall requires specialist drilling (extra work ~€199).",
  no_electrical_group: "No free group available in the fuse box (extra work ~€299).",
  panel_replacement:
    "The fuse box may need to be replaced. This is a complex installation. Contact Quatt.",
  meerwerk_l_tier:
    "The installation has 3 or more complicating factors. A standard installation is not possible. Schedule an installation check with Quatt.",
  stadsverwarming: "District heating is not compatible with a heat pump. There is no gas meter present.",
  luchtverwarming: "An air heating system is not compatible with a heat pump integration.",
  convectoren_only:
    "Floor convectors alone form a high-temperature system that is not suitable for All-Electric.",
  apartment_no_vve:
    "For an apartment, owners' association (VVE) approval is required to install an outdoor unit.",
  apartment_no_space:
    "There is no outdoor space (balcony/garden) available for placing the outdoor unit.",
};

const EN_MESSAGES: EngineMessages = {
  d: EN_DISCLAIMERS,
  no_exclusions: "No exclusions found.",
  gas_cap_detail: (m3, capM3) =>
    `Yearly gas consumption of ${m3} m3 exceeds the limit of ${capM3} m3. The heat demand is too high for All-Electric.`,
  label_block_detail: (label) =>
    `Energy label ${label} indicates insufficient insulation for All-Electric. Additional insulation measures are needed.`,
  heat_ok_single: (req, cap, waterTemp, bucket) =>
    `Suitable: Single. Heat demand of ${req} kW fits within Single capacity of ${cap} kW (W${waterTemp}, insulation ${EN_BUCKET[bucket] ?? bucket}).`,
  heat_ok_duo: (req, cap, waterTemp, bucket) =>
    `Suitable: Duo. Heat demand of ${req} kW fits within Duo capacity of ${cap} kW (W${waterTemp}, insulation ${EN_BUCKET[bucket] ?? bucket}).`,
  heat_duo_required: (cov, maxTemp) =>
    `Duo required (${cov}% coverage). Estimated max. indoor temperature ${maxTemp}°C at -7°C.`,
  heat_duo_limited: (cov, maxTemp) =>
    `Duo required with limited comfort (${cov}% coverage). Estimated max. indoor temperature ${maxTemp}°C at -7°C.`,
  heat_not_suitable: (cov, maxTemp) =>
    `Not suitable (${cov}% coverage). Estimated max. indoor temperature ${maxTemp}°C at -7°C. Additional insulation is needed.`,
  water_combo_check: (main, whole, comfort) =>
    `Combination ${EN_CONFIG_STRINGS[main] ?? main} (main room) / ${EN_CONFIG_STRINGS[whole] ?? whole} (whole house) with boiler comfort at 55C: ${comfort ? "Yes" : "No"}. Additional review needed.`,
  water_fill_radiators: (baseDetail) =>
    `${baseDetail} Enter radiator dimensions for an exact check.`,
  rad_no_data: "Insufficient heat-demand data to assess radiator capacity.",
  rad_ok: (total, needed, pct) =>
    `${total} kW delivered >= ${needed} kW required (${pct}%).`,
  rad_margin: (total, needed, pct) =>
    `${total} kW delivered, ${pct}% of ${needed} kW required (small margin).`,
  rad_insufficient: (total, needed, pct) =>
    `${total} kW delivered, ${pct}% of ${needed} kW required. Radiators deliver too little at W55.`,
  rad_combined: (baseDetail, radDetail) => `${baseDetail} Radiator check: ${radDetail}`,
  th_zone:
    "Zone thermostat: not blocking, but All-E has less backup power than a hybrid. " +
    "Zone control pushes water temperatures up in practice (lower COP) and makes the system more complex and fault-prone. " +
    "Advise having the installer review it.",
  th_extra_sources: "Additional heat sources can influence sizing",
  th_cw6: "CW6 implies a higher peak demand. Check whether this is needed",
  th_day_low: "day temperature lower than 17C",
  th_day_high: "day temperature higher than 22C",
  th_delta: (diff) => `day/night difference of ${diff}C (max 2C recommended)`,
  th_not_suitable: (red) => `Not suitable: ${red}.`,
  th_additional: (amber) => ` Additionally: ${amber}.`,
  th_extra_check: (amber) => `Additional review: ${amber}.`,
  th_ok: (day, night, delta) =>
    `Day ${day}C / night ${night}C (delta ${delta}C). Suitable for All-Electric.`,
  ins_default_ok: "Insulation level appears sufficient for All-Electric.",
  ins_score_low: (avg) =>
    `Average insulation score ${avg}/10. Additional insulation measures are needed for optimal efficiency.`,
  ins_score_mid: (avg) =>
    `Average insulation score ${avg}/10. Acceptable; better results with additional insulation.`,
  ins_score_good: (avg) => `Average insulation score ${avg}/10. Good.`,
  ins_label_despite_score: (label, avg) =>
    `Energy label ${label} despite insulation score ${avg}/10. Additional insulation is needed.`,
  ins_label_block: (label) =>
    `Energy label ${label}: insulation is probably insufficient. Additional insulation is needed for All-Electric.`,
  ins_pre1970: (count) =>
    `Home built before 1970 with ${count} insulation measure(s). At least 3 recommended. Discuss insulation options with the customer.`,
  ins_year_min: (yearLabel, count, min) =>
    `Home (${EN_CONFIG_STRINGS[yearLabel] ?? yearLabel}) with ${count} insulation measure(s). At least ${min} recommended for optimal efficiency.`,
  ins_no_data: "No insulation data entered. Check the insulation on site.",
  unknown: "Unknown",
  phys_hb_doubt: "Doubt about space for the HeatBattery. Check the dimensions on site.",
  phys_ok: "Physical installation appears feasible.",
  elec_ok: "Electrical connection appears to be in order.",
  mw_std: "Standard installation without extra work.",
  mw_s: (factor) => `Light extra work expected (~€400). Factor: ${factor}.`,
  mw_m: (count) => `Extra work expected (~€750). ${count} complicating factors.`,
  mw_factor_outdoor: (label) => `Outdoor unit: ${EN_CONFIG_STRINGS[label] ?? label}`,
  mw_factor_piping: (label) => `Piping distance: ${EN_CONFIG_STRINGS[label] ?? label}`,
  mw_factor_wall: (label) => `Wall material: ${EN_CONFIG_STRINGS[label] ?? label}`,
  mw_no_group: "No free group in the fuse box",
  mw_panel: "Fuse box replacement needed",
  mw_panel_extra: "Fuse box replacement (extra complexity)",
  mw_contact: "Contact us",
  tr: (dutch) => EN_CONFIG_STRINGS[dutch] ?? dutch,
};

const EN_BUCKET: Record<string, string> = {
  Goed: "Good",
  Gemiddeld: "Average",
  Slecht: "Poor",
};

export function getMessages(lang: Lang): EngineMessages {
  return lang === "en" ? EN_MESSAGES : NL;
}
